import { Document as RichTextDocument } from '@contentful/rich-text-types';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useState } from 'react';

import Button, { ButtonVariant } from '@/components/BaseLibrary/Button/Button';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import { Contentful, ContentStyle, LinkContent } from '@/contentful';

import FAQGroup from './FAQGroup/FAQGroup';
import useStyles from './GlobalFAQ.styles';

export interface AccordionContent extends Contentful {
  fields: {
    summary: string;
    details: RichTextDocument;
  };
}

export interface GlobalFAQContent extends Contentful {
  fields: {
    style?: ContentStyle;
    eyebrow?: string;
    heading?: string;
    faqs: AccordionContent[];
    footerText?: string;
    footerCta?: LinkContent;
  };
}

export interface GlobalFAQProps {
  content: GlobalFAQContent;
}

export default function GlobalFAQ({
  content: {
    fields: { style, eyebrow, heading, faqs, footerText, footerCta },
  },
}: GlobalFAQProps) {
  const isOnDark = style === ContentStyle.OnDark;
  const { classes } = useStyles({ isOnDark });

  const [faqState, setFaqState] = useState<boolean[]>(
    Array(faqs.length).fill(false)
  );

  const faqsWithIndex = faqs.map((faq, accordionIndex) => ({
    ...faq,
    accordionIndex,
  }));
  const faqHalfIndex = Math.ceil(faqs.length / 2);

  return (
    <SpacingContainer className={classes.container}>
      <GridContainer2 fixed>
        <Grid2 xs={12} md={6}>
          <SectionHeader
            eyebrow={eyebrow}
            heading={heading}
            isOnDark={isOnDark}
            fixed
          />
        </Grid2>
      </GridContainer2>
      <GridContainer2 fixed gridProps={{ className: classes.faqsContainer }}>
        <Grid2 xs={12} md={6}>
          <FAQGroup
            faqs={faqsWithIndex.slice(0, faqHalfIndex)}
            faqState={faqState}
            setFaqState={setFaqState}
            isOnDark={isOnDark}
          />
        </Grid2>
        <Grid2 xs={12} md={6} className={classes.lastFaqSection}>
          <FAQGroup
            faqs={faqsWithIndex.slice(faqHalfIndex)}
            faqState={faqState}
            setFaqState={setFaqState}
            isOnDark={isOnDark}
          />
        </Grid2>
      </GridContainer2>
      {(footerText || footerCta) && (
        <GridContainer2 fixed>
          <Grid2 xs={12} className={classes.footer}>
            <span>{footerText}</span>
            <Button
              label={footerCta?.fields.text}
              isOnDark={isOnDark}
              variant={ButtonVariant.SECONDARY}
              href={footerCta?.fields.url}
            />
          </Grid2>
        </GridContainer2>
      )}
    </SpacingContainer>
  );
}
